import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet";

const Legal = () => {
  const title = "Mentions Légales | NLocale - Découvrez l'Aisne Autrement";
  const description = "Mentions légales de NLocale, votre plateforme de découverte locale dans l'Aisne. Informations sur l'éditeur, l'hébergeur et vos droits.";
  const canonicalUrl = "https://onarrivebientot.nlocale.fr/mentions-legales";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": canonicalUrl,
    "mainEntity": {
      "@type": "Corporation",
      "name": "SAS WAKINAH",
      "alternateName": "NLocale",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4 rue paul Doumer",
        "addressLocality": "Laon",
        "postalCode": "02000",
        "addressCountry": "FR"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-12">
        <div className="container max-w-3xl mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="mb-8">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>

          <h1 className="text-4xl font-bold mb-8">Mentions Légales</h1>

          <div className="prose prose-blue max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Éditeur du Site</h2>
              <ul className="list-none pl-0">
                <li><strong>Raison sociale :</strong> SAS WAKINAH</li>
                <li><strong>Nom commercial :</strong> NLocale</li>
                <li><strong>Forme juridique :</strong> Société par Actions Simplifiée (SAS)</li>
                <li><strong>Siège social :</strong> 4 rue paul Doumer 02000 Laon</li>
                <li><strong>Immatriculation :</strong> RCS de Saint-Quentin, 93344099200016</li>
                <li><strong>Contact :</strong> mail@nlocale.fr</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Hébergeur du Site</h2>
              <ul className="list-none pl-0">
                <li><strong>Nom :</strong> Netlify</li>
                <li><strong>Adresse :</strong> 512 2nd Street, Suite 200 San Francisco, CA 94107</li>
                <li><strong>Contact :</strong> PO Box, PMB 87587, San Francisco, CA 94120-7775, Attn: Netlify Legal dept</li>
                <li><strong>Courriel :</strong> support@netlify.com</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Collecte et Traitement des Données Personnelles</h2>
              <p>
                Dans le cadre de son activité, NLocale est amené à collecter et traiter des données personnelles, notamment lors de l'inscription à la newsletter. 
                Ces traitements sont effectués conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Finalités du Traitement</h2>
              <p>Les données collectées sont utilisées pour :</p>
              <ul className="list-disc pl-6">
                <li>L'envoi de la newsletter et d'informations relatives aux services de NLocale</li>
                <li>La gestion des abonnements et des désabonnements</li>
                <li>L'amélioration des services proposés</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Base Légale du Traitement</h2>
              <p>
                Le traitement des données repose sur le consentement des personnes concernées, conformément à l'article 6(1)(a) du RGPD.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Droits des Personnes Concernées</h2>
              <p>Conformément à la réglementation, vous disposez des droits suivants sur vos données :</p>
              <ul className="list-disc pl-6">
                <li><strong>Droit d'accès :</strong> Obtenir la confirmation que vos données sont traitées et en recevoir une copie</li>
                <li><strong>Droit de rectification :</strong> Demander la correction de données inexactes ou incomplètes</li>
                <li><strong>Droit à l'effacement :</strong> Demander la suppression de vos données, sous certaines conditions</li>
                <li><strong>Droit à la limitation du traitement :</strong> Demander la suspension temporaire du traitement de vos données</li>
                <li><strong>Droit d'opposition :</strong> Vous opposer au traitement de vos données pour des motifs légitimes</li>
                <li><strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré et lisible</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">Contact</h2>
              <p>
                Pour exercer ces droits, veuillez contacter : <a href="mailto:mail@nlocale.fr" className="text-primary hover:underline">mail@nlocale.fr</a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Legal;
